/* eslint-disable no-nested-ternary */
import React, { useState } from 'react'
import { connect } from 'react-redux'
import { useTranslation } from 'react-i18next'
import get from 'lodash/get'
import {
  Modal,
  ContentSection,
  Paragraph,
  Button,
  FormSection,
  FormSectionGroup,
} from '@vwfs-bronson/bronson-react'
import routes from '../../routes'
import { StorefrontShoppingCart } from '../../components/ShoppingCart'
import AdditionalDrivers from './AdditionalDrivers'
import { Analytics } from '../../services/analytics'
import { history, qs } from '../../services/routing'
import { getStorefrontData } from '../../services/redux/features/storefront.redux'

import {
  saveFormData,
  getFormData,
} from '../../services/redux/features/form.redux'

import {
  setKeycloak,
  getKeycloakTokens,
} from '../../services/redux/features/keycloak.redux'
import { hasAdditionalDrivers } from '../../services/common/utils'
import { scrollToElement } from '../../services/common/form'
import PersonalDetails from './PersonalDetails'
import ContactDetails from './ContactDetails'
import { Forms } from '../../models/AppPage/Forms'
import IdentityBroker from '../../components/IdentityBroker/Login'
import {
  getActualPage,
  setIsLoggedIn as setIsLoggedInAction,
  getIsLoggedIn,
  setErrorData as setErrorDataAction,
  getContinueJourney,
  setContinueJourney as setContinueJourneyAction,
} from '../../services/redux/features/custom.redux'
import { trackConnectError } from './tracking'
import { LastFormSectionCompleted } from '../../models/enums'

const AppForm = (props) => {
  const {
    editPage,
    location,
    formData,
    storefrontData,
    continueJourney,
    setContinueJourney,
  } = props

  const { t } = useTranslation()

  const personalDetailsRef = React.useRef(undefined as any)
  const contactDetailsRef = React.useRef(undefined as any)
  const additionalDriversRef = React.useRef(undefined as any)
  const entryPointUrl = storefrontData?.salesChannel?.entryPointUrl
  const transactionId = storefrontData?.transaction?.id
  const NUMBER_OF_BUCKETS = () => {
    return hasAdditionalDrivers(storefrontData) ? 3 : 2
  }
  const [validSections, setValidSections] = React.useState(
    formData.formValidationStatus?.validSections || {}
  )
  const [openFormSection, setOpenFormSection] = React.useState(undefined as any)
  const [showErrorOnConnect, setshowErrorOnConnect] = React.useState(false)

  React.useEffect(() => {
    goSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validSections])

  React.useEffect(() => {
    if (editPage === true) {
      const ids = ['personalDetails', 'contactDetails']
      if (hasAdditionalDrivers(storefrontData)) {
        ids.splice(2, 0, 'additionalDrivers')
      }
      const sectionNumber = Number(qs(location.search, 'section'))
      getValidationStateFromSummary(ids[sectionNumber])
      setOpenFormSection(ids[sectionNumber])
    } else if (continueJourney) {
      getValidSections()
      doPrefill()
    }

    if (
      props.storefrontId ||
      get(props, 'history.location.state.storefrontId')
    ) {
      document.title = t('form:tabTitle')
    } else {
      history.pushPreservePath(routes.errorPage)
    }
    // we don't need props and t as dependency here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    Analytics.addToDatalayer({
      'core.attributes.transactionID': transactionId,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const doPrefill = async () => {
    await validatePrefilledForms(
      storefrontData?.custom?.lastFormSectionCompleted
    )
    setContinueJourney('')
  }
  const validatePrefilledForms = async (lastFormSectionCompleted: string) => {
    const validationResult: Forms = {
      personalDetails:
        lastFormSectionCompleted === LastFormSectionCompleted.PERSONAL ||
        lastFormSectionCompleted === LastFormSectionCompleted.CONTACT ||
        lastFormSectionCompleted === LastFormSectionCompleted.DRIVERS ||
        lastFormSectionCompleted === LastFormSectionCompleted.PAYMENT,
      contactDetails:
        lastFormSectionCompleted === LastFormSectionCompleted.CONTACT ||
        lastFormSectionCompleted === LastFormSectionCompleted.DRIVERS ||
        lastFormSectionCompleted === LastFormSectionCompleted.PAYMENT,
      additionalDrivers: hasAdditionalDrivers(storefrontData)
        ? lastFormSectionCompleted === LastFormSectionCompleted.DRIVERS ||
          lastFormSectionCompleted === LastFormSectionCompleted.PAYMENT
        : undefined,
    }
    await setValidSections(validationResult)
    return validationResult
  }

  const getValidationStateFromSummary = (section) => {
    const validationResult: Forms = {
      personalDetails: section !== 'personalDetails',
      contactDetails: section !== 'contactDetails',
      additionalDrivers: hasAdditionalDrivers(storefrontData)
        ? section !== 'additionalDrivers' || false
        : undefined,
    }
    setValidSections(validationResult)
  }

  const areAllFormsValid = (validation) =>
    Object.values(validation).filter((e) => e === true).length ===
    NUMBER_OF_BUCKETS()

  const goSummary = () => {
    if (areAllFormsValid(validSections)) {
      history.push('/summary')
      scrollToElement('.c-header')
      setOpenFormSection(undefined)
    }
  }

  const getValidSections = async () => {
    const validationResult: Forms = {
      personalDetails: validSections?.personalDetails || false,
      contactDetails: validSections?.contact || false,
      additionalDrivers: hasAdditionalDrivers(storefrontData)
        ? validSections?.additionalDrivers || false
        : undefined,
    }
    await setValidSections(validationResult)
    return validationResult
  }

  const getFormSectionsValidSections = () => {
    return validSections
      ? hasAdditionalDrivers(storefrontData)
        ? [
            validSections?.personalDetails || false,
            validSections?.contactDetails || false,
            validSections?.additionalDrivers || false,
          ]
        : [
            validSections?.personalDetails || false,
            validSections?.contactDetails || false,
          ]
      : new Array(hasAdditionalDrivers(storefrontData) ? 3 : 2).fill(
          validSections
        )
  }

  const updateValidSections = (section, value) => {
    const updatedSections = { ...validSections }
    updatedSections[section] = value
    setValidSections(updatedSections)
    return validSections
  }

  const onErrorFetch = () => {
    trackConnectError()
    setshowErrorOnConnect(true)
  }
  const validProps = {
    updateValidSections,
    goSummary,
    onErrorFetch,
  }

  const sections = [
    {
      id: 'personalDetails',
      trackingName: 'form:personalDetails:analyticsName',
      title: t('personal-details:title'),
      subtitle: t('personal-details:subtitle'),
      child: (
        <PersonalDetails
          {...validProps}
          ref={personalDetailsRef}
          editPage={editPage}
        />
      ),
    },
    {
      id: 'contactDetails',
      trackingName: 'form:contactDetails:analyticsName',
      title: t('contact-details:title'),
      subtitle: t('contact-details:subtitle'),
      child: <ContactDetails {...validProps} ref={contactDetailsRef} />,
    },
  ]

  const [state, setState] = useState({
    currentStepIndex: 0,
  })

  const onSectionSubmit = (index: number) => {
    setState((prevState: any) => {
      const nextIndex = index + 1
      return {
        ...prevState,
        currentStepIndex:
          nextIndex < sections.length ? nextIndex : prevState.currentStepIndex,
      }
    })
  }

  const onGotoStep = (index: number) => {
    if (index <= state.currentStepIndex) {
      setState((prevState) => ({
        ...prevState,
        currentStepIndex: index,
      }))
    }
  }

  if (hasAdditionalDrivers(storefrontData)) {
    sections.splice(2, 0, {
      id: 'additionalDrivers',
      trackingName: 'form:additionalDrivers:analyticsName',
      title: t('additional-drivers:title'),
      subtitle: t('additional-drivers:headerText'),
      child: (
        <AdditionalDrivers
          {...validProps}
          ref={additionalDriversRef}
          editPage={editPage}
        />
      ),
    })
  }

  const onClose = () => {
    setshowErrorOnConnect(false)
  }
  return (
    <>
      <Modal
        onClose={onClose}
        onClickOutside={onClose}
        title={t('error:heading')}
        shown={showErrorOnConnect}
      >
        <p> {t('error:text1')}</p>
        <Button testId="contactDetailsButton" onClick={onClose} type="button">
          {t('form:btnContinue')}
        </Button>
      </Modal>
      <ContentSection>
        <ContentSection.ComponentWrapper>
          <StorefrontShoppingCart foldout floatingBar />
        </ContentSection.ComponentWrapper>
      </ContentSection>
      <IdentityBroker entryPointUrl={entryPointUrl} />
      <ContentSection pageWrap>
        <Paragraph testId="text">
          <strong>{t('form:text')}</strong>
        </Paragraph>
        <ContentSection.ComponentWrapper>
          {!editPage || (editPage && openFormSection) ? (
            <FormSectionGroup>
              {sections
                .filter((section) => section)
                .map((section, index) => {
                  const isCurrentStep = index === state.currentStepIndex
                  const isEditPageSection =
                    editPage && section.id === openFormSection
                  const lastCompletedIndex =
                    getFormSectionsValidSections().lastIndexOf(true)
                  const defaultOpenIndex = Math.max(
                    state.currentStepIndex,
                    lastCompletedIndex + 1
                  )
                  const shouldBeOpen = editPage
                    ? section.id === openFormSection
                    : index === defaultOpenIndex
                  const shouldBeEnabled =
                    isEditPageSection ||
                    (!editPage && index <= defaultOpenIndex)
                  return (
                    <FormSection
                      key={section.id}
                      defaultOpen={shouldBeOpen}
                      id={section.id}
                      subtitle={section.subtitle}
                      title={section.title}
                      success={getFormSectionsValidSections()[index]}
                      disabled={!shouldBeEnabled}
                      onClick={() => {
                        if (isCurrentStep) {
                          onGotoStep(index)
                        }
                      }}
                    >
                      {React.cloneElement(section.child, {
                        onSubmitParent: (values) => {
                          onSectionSubmit(index)
                        },
                      })}
                    </FormSection>
                  )
                })}
            </FormSectionGroup>
          ) : (
            ''
          )}{' '}
        </ContentSection.ComponentWrapper>
      </ContentSection>
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    formData: getFormData(state),
    actualPage: getActualPage(state),
    getTokens: getKeycloakTokens(state),
    isLoggedIn: getIsLoggedIn(state),
    storefrontData: getStorefrontData(state),
    continueJourney: getContinueJourney(state),
  }
}

export default connect(mapStateToProps, {
  saveFormData,
  setKeycloak,
  setIsLoggedIn: setIsLoggedInAction,
  setErrorData: setErrorDataAction,
  setContinueJourney: setContinueJourneyAction,
})(AppForm)
